// The note-sync data core moved to `@heidi/session/data` with the D4 family
// (the W3c split); this signature-stable delegate resolves the editor flags —
// a heavy Athena/integrations composition that stays app-side — and passes the
// content-type boolean and telemetry label into the package hook. Re-exported
// symbols keep existing importers + `jest.mock` literals resolving until
// Z1's codemod.
import {
  isCompletedJsonNote,
  resolveNoteEditorType,
} from '@/container/ScribeTabs/TabPane/NoteTabPane/hooks/resolveNoteEditorType';
import { useIsFlagCohortResolved } from '@/features/flags';
import { useNoteEditorFlags } from '@/hooks/useNoteEditorFlags';
import { trackNoteUpdateAttempt } from '@/lib/telemetry/noteUpdateTracking';
import { usePosthogFlagsReady } from '@/providers/posthog-flags';
import { toast } from '@heidi/design-system/toast';
import { useCurrentSessionId } from '@heidi/runtime/shell';
import { type ContentType } from '@heidi/schema/ml-scribe';
import {
  useMutateSyncNoteToServer as useMutateSyncNoteToServerCore,
  useQuerySessionById,
  type NoteSyncOutcomeInfo,
} from '@heidi/session/data';
import { useIntl } from 'react-intl';
import { useSessionEhrProvider } from '../integrations/useSessionEhrProvider';
import {
  emitNoteSaveAction,
  NOTE_SAVE_ATTEMPTED_ACTION,
  NOTE_SAVE_SUCCEEDED_ACTION,
  UNRESOLVED_EDITOR_TYPE,
  type NoteSaveEditorType,
} from './noteSaveTelemetry';

export { SYNC_NOTE_TO_SERVER } from '@heidi/session/data';

export function useMutateSyncNoteToServer(options?: {
  contentTypeOverride?: ContentType;
}) {
  // Resolve the block-editor decision from the ACTIVE session's provider so the
  // saved content_type matches the editor the user is actually in. Without this
  // the save falls back to the first completed onboarding session and, for a
  // multi-integration user, can label a Markdown note as HTML (corrupting the
  // writeback). Off the session page useCurrentSessionId() is '' → prior fallback.
  const currentSessionId = useCurrentSessionId();
  const sessionEhrProvider = useSessionEhrProvider(currentSessionId).sessionEhrProvider;
  const intl = useIntl();

  // Bound to the same `sessionEhrProvider` as the content_type decision, so a
  // multi-integration user's telemetry label and saved payload can't disagree.
  const editorFlags = useNoteEditorFlags(sessionEhrProvider);
  const {
    data: shouldUseJSONBlockEditor,
    isPending: isSessionPending,
    isFetching: isSessionFetching,
  } = useQuerySessionById({
    sessionId: currentSessionId,
    select: (data) => (data ? isCompletedJsonNote(data.consult_note) : undefined),
  });
  // Gated on provider readiness rather than on the flag values: every editor
  // flag collapses `undefined` to `false` at source, so a value probe cannot
  // tell "off" from "not loaded" and would park pre-load saves in the
  // resolver's `textarea` floor. Both providers are checked because the two
  // highest-priority branches come from different ones — markdown-native is
  // OpenFeature, the block-editor pair is PostHog. `pending && fetching` is
  // uniquely the initial load, so a *disabled* query (offline local sessions,
  // pending forever but never fetching) still resolves a real label.
  const posthogFlagsReady = usePosthogFlagsReady();
  const flagCohortResolved = useIsFlagCohortResolved();
  const editorInputsSettled =
    posthogFlagsReady && flagCohortResolved && !(isSessionPending && isSessionFetching);
  const editorType: NoteSaveEditorType = editorInputsSettled
    ? resolveNoteEditorType({ shouldUseJSONBlockEditor, ...editorFlags })
    : UNRESOLVED_EDITOR_TYPE;

  // `editorType` rides the info the package snapshotted at dispatch, not this
  // render's value, so attempt and success always land in the same cohort.
  const emitSave =
    (action: typeof NOTE_SAVE_ATTEMPTED_ACTION | typeof NOTE_SAVE_SUCCEEDED_ACTION) =>
    (info: NoteSyncOutcomeInfo<NoteSaveEditorType>) =>
      emitNoteSaveAction(action, {
        session_id: info.sessionId,
        editor_type: info.editorType,
        content_type: info.contentType,
        content_length: info.contentLength,
      });

  return useMutateSyncNoteToServerCore<NoteSaveEditorType>({
    contentTypeOverride: options?.contentTypeOverride,
    enableBlockEditorV1: editorFlags.enableBlockEditorV1 ?? false,
    editorType,
    onSyncAttempt: emitSave(NOTE_SAVE_ATTEMPTED_ACTION),
    onSyncSuccess: emitSave(NOTE_SAVE_SUCCEEDED_ACTION),
    // One `note_update` attempt per autosave the editor marks with `autosave`.
    trackAutosave: (info, save) =>
      trackNoteUpdateAttempt({ editor: info.editorType }, save),
    // ARCFE-921 severance: the package hook is headless; the error toast is
    // an app reaction fired after the package's optimistic rollback. The
    // description stays raw — it is the backend's own error text.
    onSyncError: (error) => {
      toast({
        tone: 'destructive',
        title: intl.formatMessage({
          defaultMessage: 'Failed to update note',
          id: 'wBsNcC0rPC',
        }),
        description: error.message,
      });
    },
  });
}
